// Acciones de una fila: el traspaso se registra en Inventario, la promo de venta se crea en Promos venta
// y el diálogo señala dónde actuar en el Portal SureKeep o en el inventario global.
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { Gate, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { PROMOTIONS_PERMISSIONS } from '../../promotions/permissions';
import { salePromotionPrefillState } from '../../promotions/lib/salePromotionPrefill';

/** Las acciones que explica el diálogo; la promo de venta es un enlace propio (`CreateSalePromotionLink`). */
export type LaCentralAction = 'transfer' | 'promoSk' | 'stock';

export interface LaCentralTarget {
    action: LaCentralAction;
    /** El SKU sobre el que se acciona. Ausente en la vista global del inventario. */
    sku?: string;
    /** El lente manda ids de tienda; el nombre lo resuelve el front contra el padrón. */
    fromStoreId?: string;
    fromStoreName?: string;
    toStoreId?: string;
    toStoreName?: string;
}

const TITLES: Record<LaCentralAction, string> = {
    transfer: 'Traspaso · Registrar movimiento',
    promoSk: 'Abrir Portal SureKeep · Nueva promo',
    stock: 'Abrir La Central · Inventario global',
};

/** Nombre de la tienda si el padrón la resolvió; si no, su id, que al menos la identifica. */
function storeText(name: string | undefined, id: string | undefined): string | null {
    if (name) return name;
    return id ? `la tienda ${id}` : null;
}

/** El traspaso nombra origen y destino. Sin padrón cae al id: identificar no es inventar. */
function describeTransfer(target: LaCentralTarget): string {
    const origin = storeText(target.fromStoreName, target.fromStoreId);
    const destination = storeText(target.toStoreName, target.toStoreId);
    const from = origin ? `desde ${origin}` : 'sin tienda de origen sugerida';
    const to = destination ? `hacia ${destination}` : 'sin tienda de destino';
    return `Registra el traspaso del SKU ${target.sku ?? '—'}, ${from} ${to}, en Inventario > pestaña Stock y movimientos > Traspasar, en la fila de la tienda origen. Queda en tránsito hasta que la tienda destino lo reciba.`;
}

/** El inventario global no lleva SKU: es la vista completa de La Central, no una fila. */
function describeStock(sku: string | undefined): string {
    if (!sku) {
        return 'Se abre La Central > Inventario Global (4.2), con TODO el inventario de VentasLuga y su antigüedad promedio. Esta lente solo muestra los SKUs financiables por SureKeep, así que ahí verás más equipos que aquí.';
    }
    return `Se abre La Central > Inventario Global (4.2) filtrado por el SKU ${sku}. Muestra IMEI, antigüedad y ubicación por tienda.`;
}

function describe(target: LaCentralTarget): string {
    const sku = target.sku ?? '—';
    switch (target.action) {
        case 'transfer':
            return describeTransfer(target);
        case 'promoSk':
            return `Se abre Portal SureKeep > Promociones > Nueva promo SK para el SKU ${sku}. Sugerencia: descuento -20% al enganche por 14 días (target Plata/Oro).`;
        case 'stock':
            return describeStock(target.sku);
    }
}

const ctaClass = (variant: 'primary' | 'secondary') =>
    cn(
        'inline-flex items-center gap-[3px] whitespace-nowrap rounded px-2 py-[3px] text-[9.5px] font-semibold transition-opacity',
        variant === 'primary'
            ? 'bg-primary text-primary-foreground hover:opacity-90'
            : 'border border-border bg-card text-foreground hover:bg-accent',
    );

/** «Crear promo venta»: abre el formulario de Promos venta con el SKU ya puesto en el alcance. */
export function CreateSalePromotionLink({ sku }: { sku: string }) {
    return (
        <Gate permission={PROMOTIONS_PERMISSIONS.create}>
            <Link
                to="/retail-admin/$section"
                params={{ section: 'promociones' }}
                state={salePromotionPrefillState(sku)}
                className={ctaClass('primary')}
            >
                Crear promo venta
            </Link>
        </Gate>
    );
}

/**
 * Botón-acción de una fila. Visualmente es el `.cta` del mockup: relleno el primario, con borde el
 * secundario.
 */
export function LaCentralButton({
    target,
    variant = 'primary',
    children,
    onOpen,
}: {
    target: LaCentralTarget;
    variant?: 'primary' | 'secondary';
    children: ReactNode;
    onOpen: (target: LaCentralTarget) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => {
                onOpen(target);
            }}
            className={ctaClass(variant)}
        >
            {children}
        </button>
    );
}

/** El diálogo que explica a dónde lleva la acción. Se cierra con "Entendido", como el mockup. */
export function LaCentralDialog({
    target,
    onClose,
}: {
    target: LaCentralTarget | null;
    onClose: () => void;
}) {
    return (
        <Modal
            isOpen={target !== null}
            onClose={onClose}
            title={target ? TITLES[target.action] : ''}
            size="sm"
        >
            {target ? (
                <div className="flex flex-col gap-4">
                    <p className="text-[13px] leading-relaxed">{describe(target)}</p>
                    {target.action === 'transfer' ? null : (
                        <p className="flex items-start gap-1.5 rounded-md bg-muted/50 p-2.5 text-[11.5px] text-muted-foreground">
                            <ExternalLink className="mt-px size-3.5 flex-none" aria-hidden />
                            Esta acción se hace en el sistema de destino: aquí solo se señala dónde
                            conviene actuar.
                        </p>
                    )}
                </div>
            ) : null}
        </Modal>
    );
}

/** Estado del diálogo, para que cada tabla no repita el `useState`. */
export function useLaCentral() {
    const [target, setTarget] = useState<LaCentralTarget | null>(null);
    return {
        target,
        open: setTarget,
        close: () => {
            setTarget(null);
        },
    };
}
